import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, computed, Inject, Signal, signal, WritableSignal } from '@angular/core';
import { ButtonPadding, ButtonVariant, PlButtonType } from '@app/components/button/button.component';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { PlIcon } from '@app/icon-library/icons';
import { LifemapLog, LifemapService, PlCalendarEvent } from '@shared/services/lifemap/lifemap.service';
import { BlockLifemap } from '@shared/models/step.model';
import { ZendeskService } from '@shared/services/zendesk.service';
import { ApiUrlProgramData, CourseContentService } from '@app/courseContent/courseContent.service';
import { PlRouter } from '@shared/services/pl-router.service';
import { ParticipantProgression, ParticipantService } from '@src/app/@shared/services/participant/participant.service';
import { getRoutePath, PlRoute } from '@app/utils/routes';
import { URL_PARAM_SHOW_PROGRESS_SAVED_BANNER } from '@app/courseContent/courseContent.component';

enum Section {
  loading = 'loading',
  stepCompletedLifemapComplete = 'stepCompletedLifemapComplete',
  stepCompletedLifemapIncomplete = 'stepCompletedLifemapIncomplete',
  submitLifemap = 'submitLifemap',
  synchronisationIssues = 'synchronisationIssues',
}

export interface ModalLifemapCompletedInputs {
  events: PlCalendarEvent[];
  isLifemapLogsFullySyncedWithServer: boolean;
  lifemapBlock: BlockLifemap;
}

@Component({
  selector: 'pl-modal-lifemap-completed',
  templateUrl: './modalLifemapCompleted.component.html',
})
export class ModalLifemapCompletedComponent {
  public lifemapBlock!: WritableSignal<BlockLifemap>;
  public isLifemapLogsFullySyncedWithServer!: WritableSignal<boolean>;
  public events!: PlCalendarEvent[];
  public isLoading: WritableSignal<boolean> = signal(false);

  public sectionToDisplay: Signal<Section> = computed(() =>
    this.computeSectionToDisplay(
      this.isLoading(),
      this.courseContentService.isCurrentStepCompleted(),
      this.isLifemapLogsFullySyncedWithServer(),
    ),
  );

  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlButtonType: typeof PlButtonType = PlButtonType;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly Section: typeof Section = Section;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalLifemapCompletedInputs,
    private dialogRef: MatDialogRef<ModalLifemapCompletedComponent, void>,
    private courseContentService: CourseContentService,
    private lifemapService: LifemapService,
    private participantService: ParticipantService,
    private plRouter: PlRouter,
    private zendeskService: ZendeskService,
  ) {
    this.lifemapBlock = signal(data.lifemapBlock);
    this.events = data.events;
    this.isLifemapLogsFullySyncedWithServer = signal(data.isLifemapLogsFullySyncedWithServer);
    console.log('TONIO ModalLifemapCompletedComponent constructor data=', data);
    if (!this.isLifemapLogsFullySyncedWithServer()) {
      this.syncDataWithServer();
    }
  }

  public closeModal(): void {
    this.dialogRef.close();
  }

  public goToCurrentStep(): void {
    const participantProgression: ParticipantProgression | null = this.participantService.participantProgression();
    console.log('TONIO ModalLifemapCompletedComponent goToCurrentStep participantProgression=', participantProgression);
    if (participantProgression) {
      const routeNextStep: string = participantProgression.linkNextStep || getRoutePath(PlRoute.home);
      this.plRouter.navigate([routeNextStep]);
    } else {
      throw new Error(`participantProgression  should be available`);
    }
    this.dialogRef.close();
  }

  public async syncDataWithServer(): Promise<void> {
    console.log('TONIO ModalLifemapCompletedComponent syncDataWithServer =');
    this.isLoading.set(true);

    const allLifemapLogsFromEvents: LifemapLog[] = this.lifemapService.convertEventsToLifeMapLogs(this.events);
    if (allLifemapLogsFromEvents.length === this.lifemapService.TOTAL_NUMBER_POSSIBLE_LIFEMAP_LOGS) {
      const saveSuccessful: boolean = await this.lifemapService.saveChangedLifemapLogs(
        allLifemapLogsFromEvents,
        this.lifemapBlock().data.config.lifeMapNumber,
      );
      this.isLifemapLogsFullySyncedWithServer.set(saveSuccessful);
    }
    console.log(
      'TONIO ModalLifemapCompletedComponent syncDataWithServer allLifemapLogsFromEvents=',
      allLifemapLogsFromEvents,
    );

    this.isLoading.set(false);
  }

  public async submitLifemap(): Promise<void> {
    console.log('TONIO ModalLifemapCompletedComponent submitLifemap =');

    this.isLoading.set(true);
    await this.courseContentService.saveCurrentStepAndRefreshMenu(
      ApiUrlProgramData.loggedIn,
      [],
      [this.lifemapBlock()],
      true,
    );
    this.plRouter.navigate([this.courseContentService.currentStepData()?.next_page_url], {
      queryParams: { [URL_PARAM_SHOW_PROGRESS_SAVED_BANNER]: true },
    });

    this.closeModal();
    this.isLoading.set(false);
  }

  public contactSupport(): void {
    this.zendeskService.contactUs();
    console.log('TONIO ModalLifemapCompletedComponent submitLifemap =');
  }

  private computeSectionToDisplay(
    isLoading: boolean,
    isStepCompleted: boolean,
    isLifemapLogsFullySyncedWithServer: boolean,
  ): Section {
    console.log(
      'TONIO ModalLifemapCompletedComponent computeSectionToDisplay isLoading=',
      isLoading,
      '   isStepCompleted=',
      isStepCompleted,
      '  isLifemapLogsFullySyncedWithServer=',
      isLifemapLogsFullySyncedWithServer,
    );
    if (isLoading) {
      return Section.loading;
    } else if (isStepCompleted) {
      return isLifemapLogsFullySyncedWithServer
        ? Section.stepCompletedLifemapComplete
        : Section.stepCompletedLifemapIncomplete;
    } else if (isLifemapLogsFullySyncedWithServer) {
      return Section.submitLifemap;
    } else {
      return Section.synchronisationIssues;
    }
  }
}
